/* Dr.Primal — dp-media's looping video.
 *
 * `dp-media` renders `<video>` with no `autoplay` attribute, so the browser shows the
 * poster and nothing moves until this file says so. That is deliberate on three counts:
 *
 * 1. The poster is the no-JavaScript rendering. A still of the lit liver is a complete
 *    picture, so the band degrades to an image rather than to an empty frame.
 * 2. Playback is gated on an IntersectionObserver — the loop runs only while a quarter of
 *    the frame is on screen and the tab is visible. This is the theme's convention for a
 *    decorative loop, from the design handoff's turntable note. Off screen it is paused,
 *    not merely invisible, so it costs nothing to decode.
 * 3. `prefers-reduced-motion` is checked live, on every sync and on the media query's own
 *    `change` event, never read once at load. A visitor who turns the preference on
 *    mid-session gets the still back, and one who turns it off gets the loop, without a
 *    reload either way.
 *
 * Under reduced motion the element is rewound with `load()`, not with `currentTime = 0`.
 * Frame 0 of the anatomy loop is near-blank by design — that is what makes the loop
 * seamless — so seeking to it would replace the picture with an empty ground. `load()`
 * puts the poster back, which is the frame the still is supposed to be.
 *
 * This band carries no visible pause control: they were removed theme-wide on client
 * instruction (docs/accessibility.md). Reduced motion is therefore the whole mitigation
 * here, which is why it is checked before anything is allowed to play.
 *
 * ONE honest failure mode, and it must stay one. A blocked autoplay leaves the poster up,
 * which is a correct rendering of this band. Everything else — a 404 src, a codec this
 * browser cannot play, a decode error, a dropped connection — used to land on that same
 * still, so a broken deploy was byte-identical to a working page. Every one of those now
 * paints the theme's `.dp-contract-error` marker in the frame instead.
 */
(function () {
  'use strict';

  /* Two identical `<script src>` tags — one section, one kitchen sink — both execute.
     Today the `-ready` attribute makes the second IIFE's init() a no-op, which is working
     by accident; this is the theme's actual convention (dp-parallax.js, dp-header-morph.js). */
  if (window.dpMediaVideoLoaded) return;
  window.dpMediaVideoLoaded = true;

  /* A quarter of the frame. The square is capped at 520px on desktop and is roughly a
     viewport wide on the narrowest phones, so a quarter of it is always reachable on any
     real screen. It is read off `entry.intersectionRatio`, never off `isIntersecting` —
     that is true at a single pixel regardless of `threshold`, which only decides when the
     callback fires. The `0` in the threshold list is required: without it the browser
     never reports the frame leaving and the loop would keep running off screen. */
  var VISIBLE = 0.25;

  var instances = [];

  function sourceOf(video) {
    return video.currentSrc || video.getAttribute('src') || '(no src)';
  }

  /* Paints the theme's loud author-error marker in place of the frame, so a broken video
     is distinguishable from a working page at a glance rather than only in the console.
     Latched: a failing element fires `error` more than once. */
  function fail(video, message) {
    if (video.getAttribute('data-dp-media-video-failed') === 'true') return;
    video.setAttribute('data-dp-media-video-failed', 'true');

    video.pause();
    video.hidden = true;

    if (video.parentNode) {
      var marker = document.createElement('span');
      marker.className = 'dp-contract-error';
      marker.textContent = 'dp-media video: ' + message;
      video.parentNode.appendChild(marker);
    }

    if (window.console && window.console.error) {
      window.console.error('dp-media video: ' + message, sourceOf(video));
    }
  }

  /* MediaError codes, spelled out. A wrong filename resolves to a well-formed CDN URL that
     404s, and the 404 body is not a media type, so it arrives here as code 4 rather than as
     anything that names the missing file. */
  function mediaErrorMessage(video) {
    var code = video.error ? video.error.code : 0;
    if (code === 1) return 'loading was aborted';
    if (code === 2) return 'network error while loading';
    if (code === 3) return 'the file could not be decoded';
    if (code === 4) return 'file missing, or a format this browser cannot play';
    return 'the file could not be loaded';
  }

  function bind(video) {
    if (video.getAttribute('data-dp-media-video-ready') === 'true') return;
    video.setAttribute('data-dp-media-video-ready', 'true');

    var reducedQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    var observer = null;
    /* With no IntersectionObserver there is no gate to open, so the loop plays whenever
       the tab is visible. A browser without one is not a reason to ship a dead frame. */
    var onScreen = typeof IntersectionObserver !== 'function';
    /* Latches the one `load()` per reduced-motion entry — see sync(). */
    var rewound = false;

    /* Fails CLOSED. With no `matchMedia` the preference cannot be read, and reduced motion
       is this band's whole accessibility mitigation because it carries no pause control.
       An unreadable preference therefore has to mean "hold the still", not "play anyway". */
    function reduced() {
      return !reducedQuery || !!reducedQuery.matches;
    }

    function sync() {
      if (video.getAttribute('data-dp-media-video-failed') === 'true') return;

      if (reduced()) {
        /* Read BEFORE pause(), and keyed on the element having left HAVE_NOTHING rather
           than on `currentTime > 0`. The browser paints frame 0 — the near-blank one — as
           soon as it reaches HAVE_CURRENT_DATA, before time has advanced, so a visitor who
           flips the preference on inside that window used to get the empty ground with no
           poster and no caption: the guard defeated the bug it was written to prevent.
           `rewound` keeps this to one call per entry: load() re-enters HAVE_NOTHING and
           `preload="metadata"` climbs straight back out of it, so readyState on its own
           would re-fetch on every scroll tick. */
        var painted = video.readyState > 0 || !video.paused;
        video.pause();
        if (painted && !rewound) {
          rewound = true;
          video.load();
        }
        return;
      }

      rewound = false;

      if (!onScreen || document.hidden) {
        video.pause();
        return;
      }

      video.muted = true;
      var started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(function (err) {
          var name = err && err.name;
          /* The only two correct renderings. `NotAllowedError` is the autoplay policy, and
             iOS Low Power Mode, which leaves the poster up by design. `AbortError` is the
             ordinary race of a pause() landing between a fast scroll's play() and its
             first frame — the next sync() plays it again. Everything else is a defect:
             NotSupportedError (a 404 src, or HEVC in an .mp4), EncodingError, and
             InvalidStateError on a detached element. */
          if (name === 'NotAllowedError' || name === 'AbortError') return;
          fail(video, (name || 'playback failed') + ' — ' + ((err && err.message) || 'no detail'));
        });
      }
    }

    function onVisibility() {
      sync();
    }

    /* A src that fails before play() is ever called — the whole 404 case, because
       `preload="metadata"` starts fetching immediately — produces no promise to reject.
       This is the only channel that catches it. */
    function onError() {
      fail(video, mediaErrorMessage(video));
    }

    /* Not a failure: the fetch has gone quiet and may recover. It gets a console channel
       rather than the marker, so a slow connection is not reported as a broken deploy. */
    function onStalled() {
      if (window.console && window.console.warn) {
        window.console.warn('dp-media video: stalled fetching', sourceOf(video));
      }
    }

    video.addEventListener('error', onError);
    video.addEventListener('stalled', onStalled);
    document.addEventListener('visibilitychange', onVisibility);

    if (reducedQuery && reducedQuery.addEventListener) {
      reducedQuery.addEventListener('change', sync);
    }

    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        function (entries) {
          onScreen = entries[entries.length - 1].intersectionRatio >= VISIBLE;
          sync();
        },
        { threshold: [0, VISIBLE] }
      );
      observer.observe(video);
    }

    /* `error` can have fired before this file ran — `preload="metadata"` starts the fetch
       at parse time and the script is deferred. The event does not replay, so the element
       is asked directly. */
    if (video.error) onError();

    sync();

    instances.push({
      root: video,
      destroy: function () {
        video.pause();
        if (observer) observer.disconnect();
        video.removeEventListener('error', onError);
        video.removeEventListener('stalled', onStalled);
        document.removeEventListener('visibilitychange', onVisibility);
        if (reducedQuery && reducedQuery.removeEventListener) {
          reducedQuery.removeEventListener('change', sync);
        }
      },
    });
  }

  /* `shopify:section:unload` fires only when a section is deleted or hidden. A settings
     change re-renders the section's DOM and fires `load` alone, so the discarded <video>
     never reaches destroy() — leaking an observer holding a detached node, a
     `visibilitychange` listener, a `prefers-reduced-motion` listener and an `instances`
     entry on every edit. Dropping the detached entries on the way back in is what actually
     covers the editor's common case; the unload handler covers only delete and hide. */
  function prune() {
    instances = instances.filter(function (instance) {
      if (instance.root.isConnected === false) {
        instance.destroy();
        return false;
      }
      return true;
    });
  }

  function init() {
    prune();
    var videos = document.querySelectorAll('[data-dp-media-video]');
    Array.prototype.forEach.call(videos, bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders a section's DOM without re-executing `<script src>`. The
     `-ready` guard is what stops a second bind stacking a second observer on the same
     element; without the unload handler the observer bound to the discarded DOM would
     keep firing sync() against a detached node. */
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      if (event.target.contains(instance.root)) {
        instance.destroy();
        return false;
      }
      return true;
    });
  });
})();
